import { useApolloClient } from "@apollo/client/react";
import { map } from "lodash";
import { useCallback, useState } from "react";

import {
  peopleSearch,
  getBasePeopleByIds,
  PersonSearchBaseFragment,
} from "src/queries/person.graphql";
import { useDebouncedPersonSearch } from "src/hooks/useDebouncedPersonSearch";
import { fullDisplayName } from "src/util/personName";

export const createPersonOptions = (people) => {
  return map(people, (person) => {
    return {
      label: fullDisplayName(person),
      value: person.aggregateId,
      person,
    };
  });
};

export const useSearchPerson = ({ fetchPolicy = "no-cache" } = {}) =>
  useDebouncedPersonSearch({
    query: peopleSearch,
    mapPeopleToOptions: createPersonOptions,
    fetchPolicy,
  });

export const useLoadPeople = () => {
  const client = useApolloClient();
  const [isLoading, setIsLoading] = useState(false);

  const loadPeople = useCallback(
    async (ids) => {
      setIsLoading(true);
      const {
        data: { people },
      } = await client.query({
        query: getBasePeopleByIds,
        variables: {
          ids,
        },
      });

      setIsLoading(false);
      return people;
    },
    [client]
  );

  return {
    loadPeople,
    isLoading,
    client,
  };
};

export const useCachedPerson = () => {
  const client = useApolloClient();

  const getCachedPerson = useCallback(
    (aggregateId) => {
      const person = client.readFragment({
        id: `Person:${aggregateId}`,
        fragment: PersonSearchBaseFragment,
        fragmentName: "PersonSearchBaseFragment",
      });

      return person;
    },
    [client]
  );

  return {
    getCachedPerson,
    client,
  };
};
